import React, { useState } from 'react';
import config from '../../../config.js';
import { extractTwitchChannel } from '../../../_helpers/bandUtils.js';
import { EMBED_ALLOW, TWITCH_EMBED_ALLOW, isSafeEmbedUrl } from '../../../_helpers/streamSource.js';
import { useT } from '../../../contexts/LocaleContext.jsx';
import { useBroadcastSurface } from '../../../contexts/BroadcastSurface.jsx';

// Re-exported so existing imports keep working; the implementation is shared
// with the stream page's VideoPlayer via _helpers/streamSource.js.
export { isSafeEmbedUrl };

/**
 * Branded artwork for the stream area when there is nothing to play (same
 * "World Stage" illustration the intro page uses). Served from /public like the
 * logo, so it is never hashed away between builds.
 */
export const STREAM_PLACEHOLDER_SRC = '/assets/images/WorldStage-nobg.png';

/**
 * Player-only stream embed for the dashboard cards. While the band is live it
 * frames the player URL the server built from the band's configured stream
 * source (`embedUrl` on `/public/bands/{id}/stream/status`); that is the
 * single source of truth and always wins. Only when status carries no URL
 * does it fall back to a Twitch channel found in the band's social links
 * (bands that pre-date stream sources), and to a branded placeholder
 * otherwise.
 *
 * The placeholder always shows an image plus a short status line (ENG-323):
 * with nobody live the section used to collapse to a bare ink-coloured box,
 * which is the most plausible source of the report that the stream section
 * "doesn't load" (the report itself could not be reproduced against a
 * backend). The band's profile art is preferred when it
 * exists and actually loads; a missing or broken image falls back to the
 * World Stage illustration rather than leaving the area blank.
 *
 * Both iframes take their `allow` lists from _helpers/streamSource.js, shared
 * with the stream page's VideoPlayer so the two cannot drift again (ENG-347);
 * the rationale for what is granted, and why `allowfullscreen` is kept as
 * well, lives there.
 */
const HomeStreamEmbed = ({ band, isLive = false, embedUrl = null, className = '' }) => {
  const t = useT();
  const twitchChannel = extractTwitchChannel(band);
  // While it shows a video frame, app-level overlays must not cover this page.
  useBroadcastSurface((isLive && isSafeEmbedUrl(embedUrl)) || Boolean(twitchChannel));
  const classes = `listener-home-embed${className ? ` ${className}` : ''}`;
  const profileArt = band?.ProfileImageURL || null;
  // Remember which URL failed rather than a bare flag: a new band (or a new
  // image URL) then gets a fresh chance to load in the same render, with no
  // effect-driven correction frame showing the wrong image first.
  const [failedArt, setFailedArt] = useState(null);
  const artFailed = profileArt !== null && failedArt === profileArt;

  if (isLive && isSafeEmbedUrl(embedUrl)) {
    return (
      <div className={classes}>
        <iframe
          className="listener-home-embed-frame"
          src={embedUrl}
          title={t('home.stream.title', { name: band?.Name ?? t('home.fallbacks.performer') })}
          sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"
          allow={EMBED_ALLOW}
          allowFullScreen
        />
      </div>
    );
  }

  if (twitchChannel) {
    const parent = config.twitchParentDomain || window.location.hostname;
    // autoplay stays constant: Twitch handles the offline state itself, and
    // baking live status into the src would reload the player on every flip.
    return (
      <div className={classes}>
        <iframe
          className="listener-home-embed-frame"
          src={`https://player.twitch.tv/?channel=${encodeURIComponent(twitchChannel)}&parent=${encodeURIComponent(parent)}&muted=true&autoplay=true`}
          allowFullScreen
          allow={TWITCH_EMBED_ALLOW}
          title={t('home.stream.title', { name: band?.Name ?? twitchChannel })}
        />
      </div>
    );
  }

  const showProfileArt = Boolean(profileArt) && !artFailed;
  const statusText = isLive ? t('home.stream.liveNow') : t('home.stream.notLive');

  return (
    <div
      className={`${classes} listener-home-embed--placeholder`}
      data-testid="home-embed-placeholder"
      role="img"
      aria-label={t('home.stream.placeholderLabel', { name: band?.Name ?? t('home.fallbacks.performer'), status: statusText })}
    >
      {showProfileArt ? (
        <img
          src={profileArt}
          alt=""
          className="listener-home-embed-art"
          onError={() => setFailedArt(profileArt)}
        />
      ) : (
        <img
          src={STREAM_PLACEHOLDER_SRC}
          alt=""
          decoding="async"
          className="listener-home-embed-placeholder-image"
          data-testid="home-embed-placeholder-image"
        />
      )}
      <span className="listener-home-embed-caption" aria-hidden="true">{statusText}</span>
    </div>
  );
};

export default HomeStreamEmbed;
